import {
  Input,
  Label,
  Button,
  Badge,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@yeshuman/sdk'
"use client"


export interface SearchCriteriaFormProps {
  requiredSkills: string[]
  preferredSkills: string[]
  instructions: string
  location: string
  experienceLevel: string
  skillInput: string
  skillType: 'required' | 'preferred'
  onRequiredSkillsChange: (skills: string[]) => void
  onPreferredSkillsChange: (skills: string[]) => void
  onInstructionsChange: (instructions: string) => void
  onLocationChange: (location: string) => void
  onExperienceLevelChange: (level: string) => void
  onSkillInputChange: (input: string) => void
  onSkillTypeChange: (type: 'required' | 'preferred') => void
  onAddSkill: () => void
  onRemoveSkill: (skill: string, type: 'required' | 'preferred') => void
}

export function SearchCriteriaForm({
  requiredSkills,
  preferredSkills,
  instructions,
  location,
  experienceLevel,
  skillInput,
  skillType,
  onRequiredSkillsChange: _onRequiredSkillsChange,
  onPreferredSkillsChange: _onPreferredSkillsChange,
  onInstructionsChange,
  onLocationChange,
  onExperienceLevelChange,
  onSkillInputChange,
  onSkillTypeChange,
  onAddSkill,
  onRemoveSkill,
}: SearchCriteriaFormProps) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Add skills</Label>
        <div className="flex gap-2">
          <Input
            value={skillInput}
            onChange={(e) => onSkillInputChange(e.target.value)}
            placeholder="Type skill name..."
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                onAddSkill()
              }
            }}
          />
          <Select
            value={skillType}
            onValueChange={(value: 'required' | 'preferred') =>
              onSkillTypeChange(value)
            }
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="required">Required</SelectItem>
              <SelectItem value="preferred">Preferred</SelectItem>
            </SelectContent>
          </Select>
          <Button type="button" onClick={onAddSkill}>
            Add
          </Button>
        </div>
      </div>

      {requiredSkills.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm">Required skills</Label>
          <div className="flex flex-wrap gap-2">
            {requiredSkills.map((skill) => (
              <Badge
                key={skill}
                variant="default"
                className="flex items-center gap-1"
              >
                {skill}
                <button
                  type="button"
                  onClick={() => onRemoveSkill(skill, 'required')}
                  className="ml-1 hover:text-destructive"
                >
                  ×
                </button>
              </Badge>
            ))}
          </div>
        </div>
      )}

      {preferredSkills.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm">Preferred skills</Label>
          <div className="flex flex-wrap gap-2">
            {preferredSkills.map((skill) => (
              <Badge
                key={skill}
                variant="outline"
                className="flex items-center gap-1"
              >
                {skill}
                <button
                  type="button"
                  onClick={() => onRemoveSkill(skill, 'preferred')}
                  className="ml-1 hover:text-destructive"
                >
                  ×
                </button>
              </Badge>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label>Search instructions</Label>
        <Textarea
          value={instructions}
          onChange={(e) => onInstructionsChange(e.target.value)}
          placeholder="e.g., Prioritize candidates with startup experience. Avoid frequent job changers."
          rows={3}
          className="resize-none"
        />
        <p className="text-xs text-muted-foreground">
          Provide guidance for the AI search agent (e.g., priorities, exclusions,
          cultural fit)
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>Location</Label>
          <Input
            value={location}
            onChange={(e) => onLocationChange(e.target.value)}
            placeholder="e.g., Remote, London"
          />
        </div>
        <div className="space-y-2">
          <Label>Experience level</Label>
          <Select value={experienceLevel} onValueChange={onExperienceLevelChange}>
            <SelectTrigger>
              <SelectValue placeholder="Any level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any level</SelectItem>
              <SelectItem value="junior">Junior</SelectItem>
              <SelectItem value="mid">Mid</SelectItem>
              <SelectItem value="senior">Senior</SelectItem>
              <SelectItem value="executive">Executive</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  )
}
